'use client';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { DashboardPage } from '../dashboard-page';
import type { EntityViewConfig } from './entity-view-types';

/** Record views use the same width, gutters, title and scroll ownership as lists. */
export function EntityView({
  config,
  children,
}: { config: EntityViewConfig; children: ReactNode }) {
  const {
    identity,
    back,
    actions,
    tabs,
    activeTab,
    onTabChange,
    aside,
    asideFirstOnMobile,
  } = config;
  const isMobile = useIsMobile();
  return (
    <DashboardPage
      title={identity.title}
      titleLeading={
        identity.initials ? (
          <Avatar className="size-10 shrink-0">
            <AvatarFallback className="bg-muted font-medium">
              {identity.initials}
            </AvatarFallback>
          </Avatar>
        ) : undefined
      }
      description={
        identity.subtitle || identity.status ? (
          <>
            {identity.subtitle}
            {identity.status ? (
              <span className="ml-2">{identity.status}</span>
            ) : null}
          </>
        ) : undefined
      }
      back={back}
      actions={isMobile ? undefined : actions}
      fillHeight
      toolbar={
        <div className="shrink-0 space-y-3">
          {isMobile && actions ? (
            <div className="flex flex-wrap gap-2">{actions}</div>
          ) : null}
          {isMobile ? (
            <Select value={activeTab} onValueChange={onTabChange}>
              <SelectTrigger aria-label="Record section" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tabs.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <EntityTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
          )}
        </div>
      }
      contentClassName={cn(
        'min-h-0 flex-1 overflow-y-auto',
        isMobile && 'px-4 pb-4'
      )}
    >
      <div className="flex min-w-0 flex-col gap-6 md:flex-row">
        <div className="min-w-0 flex-1">{children}</div>
        {aside ? (
          <aside
            className={cn(
              'w-full shrink-0 md:w-[269px]',
              asideFirstOnMobile && 'order-first md:order-none'
            )}
          >
            {aside}
          </aside>
        ) : null}
      </div>
    </DashboardPage>
  );
}

/** Explicit controls reveal the tabs beyond the viewport, even with hidden OS scrollbars. */
function EntityTabs({
  tabs,
  activeTab,
  onTabChange,
}: Pick<EntityViewConfig, 'tabs' | 'activeTab' | 'onTabChange'>) {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () =>
      setEdges({
        left: node.scrollLeft > 1,
        right: node.scrollWidth - node.clientWidth - node.scrollLeft > 1,
      });
    const observer = new ResizeObserver(update);
    observer.observe(node);
    if (contentRef.current) observer.observe(contentRef.current);
    node.addEventListener('scroll', update, { passive: true });
    update();
    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', update);
    };
  }, []);
  return (
    <div className="flex min-w-0 items-center border-b">
      {edges.left || edges.right ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          aria-label="Scroll sections left"
          disabled={!edges.left}
          onClick={() =>
            ref.current?.scrollBy({ left: -240, behavior: 'smooth' })
          }
        >
          <ChevronLeftIcon className="size-4" />
        </Button>
      ) : null}
      <div
        ref={ref}
        className="flex min-w-0 flex-1 gap-1 overflow-x-auto"
        role="tablist"
        aria-label="Sections"
      >
        <div ref={contentRef} className="flex w-max gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === activeTab}
              onClick={() => onTabChange(t.id)}
              className={cn(
                'shrink-0 whitespace-nowrap border-b-2 px-3 py-3 font-medium text-sm transition-colors',
                t.id === activeTab
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {t.label}
              {t.badge}
            </button>
          ))}
        </div>
      </div>
      {edges.left || edges.right ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          aria-label="Scroll sections right"
          disabled={!edges.right}
          onClick={() =>
            ref.current?.scrollBy({ left: 240, behavior: 'smooth' })
          }
        >
          <ChevronRightIcon className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The aside's standard shape: a titled card of label/value facts. Features can
 * pass anything, but using this keeps every entity's aside recognisable.
 */
export function EntityViewAside({
  title = 'At a glance',
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4 rounded-xl border bg-background p-5">
      <h2 className="font-medium text-sm">{title}</h2>
      {children}
    </div>
  );
}
